import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const Home = () => {
    const location = useLocation();

    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    useEffect(() => {
        // Handle hash navigation (e.g., /#features)
        if (location.hash) {
            const sectionId = location.hash.substring(1);
            setTimeout(() => {
                scrollToSection(sectionId);
            }, 100);
        }
    }, [location]);

    return (
        <div className="App">
            {/* Hero Section */}
            <section id="home" className="hero">
                <div className="container">
                    <h1>Welcome to TerraCodeCli</h1>
                    <p>by TerraAGI - The next-generation AI-powered CLI tool for developers</p>
                    <div className="hero-buttons">
                        <button onClick={() => scrollToSection('features')} className="btn">
                            Explore Features
                        </button>
                        <button onClick={() => scrollToSection('about')} className="btn btn-secondary">
                            Learn More
                        </button>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section id="features" className="section">
                <div className="container">
                    <div className="section-header animate-on-scroll">
                        <h2>Powerful Features</h2>
                        <p>Built on top of Qwen's CLI with enhanced capabilities</p>
                    </div>

                    <div className="grid grid-2">
                        <div className="feature-card">
                            <div className="feature-icon">🧠</div>
                            <h3>Enterprise Knowledge Management</h3>
                            <p>
                                Upload architecture docs, API specs, and deployment guides. Interactive Knowledge Transfer sessions make Terra work like your tech lead.
                            </p>
                        </div>

                        <div className="feature-card">
                            <div className="feature-icon">🔍</div>
                            <h3>Intelligent Code Analysis</h3>
                            <p>
                                Semantic code search across your entire codebase. Find functions, patterns, and relationships using natural language queries.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* About Section */}
            <section id="about" className="section">
                <div className="container">
                    <div className="about-content">
                        <h2 className="animate-on-scroll">About TerraCodeCli</h2>
                        <p className="about-description animate-on-scroll">
                            TerraCodeCli is an open-source command line interface (CLI) tool designed to
                            enhance developer productivity through artificial intelligence. Built by TerraAGI
                            on top of Qwen's CLI product, TerraCodeCli extends the capabilities with two
                            powerful features that make AI assistance more contextual and efficient.
                        </p>

                        <div className="tech-grid">
                            <div className="tech-card">
                                <h4>Built on Qwen</h4>
                                <p>
                                    TerraCodeCli leverages the robust foundation of Qwen's CLI product,
                                    a proven command line tool that helps developers write code more
                                    efficiently.
                                </p>
                            </div>
                            <div className="tech-card">
                                <h4>Open Source</h4>
                                <p>
                                    As an open-source project, TerraCodeCli is developed by TerraAGI
                                    transparently with community contributions for continuous improvement.
                                </p>
                            </div>
                            <div className="tech-card">
                                <h4>AI-Powered</h4>
                                <p>
                                    The tool integrates advanced AI capabilities to understand context,
                                    process knowledge bases, and provide intelligent assistance.
                                </p>
                            </div>
                        </div>

                        <div className="use-cases">
                            <h3 className="animate-on-scroll">Use Cases</h3>
                            <div className="use-cases-grid">
                                <div className="use-case">
                                    <h4>Knowledge Base Integration</h4>
                                    <p>
                                        Upload documentation, wikis, or manually input knowledge to give
                                        the AI better context about your project and business logic.
                                    </p>
                                </div>
                                <div className="use-case">
                                    <h4>Repository Analysis</h4>
                                    <p>
                                        Let TerraCodeCli pre-process your entire codebase to understand patterns,
                                        dependencies, and architecture for more accurate assistance.
                                    </p>
                                </div>
                                <div className="use-case">
                                    <h4>Context-Aware Development</h4>
                                    <p>
                                        Get AI assistance that understands your specific project context,
                                        making suggestions and solutions more relevant and effective.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section id="cta" className="cta-section">
                <div className="container">
                    <div className="cta-content">
                        <h2>Ready to Get Started?</h2>
                        <p>Join the future of AI-powered development tools</p>
                        <div className="cta-buttons">
                            <a
                                href="https://github.com/TerraAGI/terra-code-cli"
                                className="btn"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                View on GitHub
                            </a>
                            <a
                                href="/install"
                                className="btn btn-secondary"
                            >
                                Installation Guide
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Contact Section */}
            <section id="contact" className="contact-section">
                <div className="container">
                    <div className="contact-content">
                        <h2>Get in Touch</h2>
                        <p>Have questions or need support? We're here to help!</p>
                        <div className="contact-info">
                            <a href="mailto:info@terra-agi.com" className="btn">
                                📧 info@terra-agi.com
                            </a>
                            <a href="https://www.linkedin.com/company/terra-agi/posts/?feedView=all" className="btn btn-secondary" target="_blank" rel="noopener noreferrer">
                                💼 LinkedIn
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;
